// Stage4LiveCandles.jsx — v1.8.0 — 2026-06-29
// Changes: feed column left, auto-zoom Stop/T1, P&L+R context lines,
//          position status indicator, unsubscribed section, RESET/per-ticker remove
import React, { useState, useEffect, useRef, useCallback } from 'react';

function toChartTime(isoString) {
  const d = new Date(isoString);
  const utcSeconds = Math.floor(d.getTime() / 1000);
  const offsetMatch = isoString.match(/([+-])(\d{2}):(\d{2})$/);
  let offsetSeconds = 0;
  if (offsetMatch) {
    const sign = offsetMatch[1] === '-' ? -1 : 1;
    offsetSeconds = sign * (parseInt(offsetMatch[2]) * 3600 + parseInt(offsetMatch[3]) * 60);
  }
  return utcSeconds + offsetSeconds;
}

export default function Stage4LiveCandles() {
  const [isWsConnected, setIsWsConnected]   = useState(false);
  const [wsHasData, setWsHasData]           = useState(false);
  const [tickerMap, setTickerMap]           = useState({});
  const [selectedTicker, setSelectedTicker] = useState(null);
  const [plans, setPlans]                   = useState([]);
  const [plansFile, setPlansFile]           = useState(null);
  const [plansLoading, setPlansLoading]     = useState(false);
  const [plansError, setPlansError]         = useState(null);
  const plansRef                            = useRef([]);
  const [activeLevels, setActiveLevels]     = useState(null);
  const [chartReady, setChartReady]         = useState(false);
  const [chartCreated, setChartCreated]     = useState(false);
  const [closedTickers, setClosedTickers]   = useState({});
  const [feedLog, setFeedLog]               = useState([]);
  const [positionMap, setPositionMap]       = useState({});  // { RPAY: 'ACTIVE', VIVS: 'WATCHING' }

  const chartContainerRef = useRef(null);
  const chartRef          = useRef(null);
  const candleSeriesRef   = useRef(null);
  const vwapSeriesRef     = useRef(null);
  const priceLineRefs     = useRef({});
  const wsRef             = useRef(null);
  const tickerMapRef      = useRef({});
  tickerMapRef.current    = tickerMap;
  const positionMapRef    = useRef({});
  positionMapRef.current  = positionMap;
  const activeLevelsRef   = useRef(null);
  const feedEndRef        = useRef(null);

  const API_BASE = process.env.REACT_APP_API_URL || 'https://daytrader.thecreativecoefficient.com.au';
  const API_KEY  = process.env.REACT_APP_API_KEY;

  const C = {
    ink:    '#09111f',
    haze:   '#1a2840',
    textLo: '#8a9bb2',
    signal: '#3a9e7e',
    warn:   '#c94f4a',
    rule:   '#b8892a',
  };

  useEffect(() => { activeLevelsRef.current = activeLevels; }, [activeLevels]);
  useEffect(() => { feedEndRef.current?.scrollIntoView({ behavior: 'auto' }); }, [feedLog]);

  useEffect(() => {
    if (window.LightweightCharts) { setChartReady(true); return; }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/lightweight-charts@4.1.0/dist/lightweight-charts.standalone.production.js';
    script.async = true;
    script.onload  = () => setChartReady(true);
    script.onerror = () => console.error('[S4] Failed to load Charts CDN');
    document.body.appendChild(script);
    return () => { if (document.body.contains(script)) document.body.removeChild(script); };
  }, []);

  useEffect(() => {
    const autoFetch = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/stage4/plans`, { headers: { 'X-API-Key': API_KEY } });
        if (!res.ok) return;
        const data = await res.json();
        const loaded = data.plans || [];
        plansRef.current = loaded; setPlans(loaded); setPlansFile(data.source_file || null);
        setSelectedTicker(prev => {
          if (prev) { const plan = loaded.find(p => p.symbol === prev); if (plan) setActiveLevels({ entry: plan.entry_price, stop: plan.stop_price, t1: plan.t1_price, t2: plan.t2_price, t3: plan.t3_price, direction: plan.direction, conviction: plan.conviction, source: 'm12' }); }
          return prev;
        });
      } catch (e) {}
    };
    autoFetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const wsUrl = API_BASE.replace('https://', 'wss://').replace('http://', 'ws://');
    const ws = new WebSocket(`${wsUrl}/ws/stage4/candles`);
    ws.onopen  = () => setIsWsConnected(true);
    ws.onclose = () => setIsWsConnected(false);
    ws.onerror = () => setIsWsConnected(false);
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        const isClosed =
          msg.type === 'ticker_closed' || msg.type === 'ticker_skipped' ||
          (msg.type === 'agent_status' && ['CLOSED','SKIPPED','SKIP','STOPPED'].includes((msg.status||'').toUpperCase()));
        if (isClosed) {
          const ticker = msg.ticker || msg.symbol;
          const reason = (msg.reason || msg.status || 'CLOSED').toUpperCase();
          if (ticker) {
            const now = new Date();
            const t = `${now.getHours().toString().padStart(2,'0')}:${now.getMinutes().toString().padStart(2,'0')}`;
            setClosedTickers(prev => ({ ...prev, [ticker]: { reason, time: t } }));
          }
          return;
        }

        // ── Position status (ACTIVE / WATCHING) ──────────────────────────────
        if (msg.type === 'position_update' || (msg.type === 'agent_status' && msg.status)) {
          const ticker = msg.ticker || msg.symbol;
          const status = (msg.status || '').toUpperCase();
          if (ticker && ['ACTIVE','FILLED','ENTERED'].includes(status)) {
            setPositionMap(prev => ({ ...prev, [ticker]: 'ACTIVE' }));
          } else if (ticker && status === 'WATCHING') {
            setPositionMap(prev => ({ ...prev, [ticker]: 'WATCHING' }));
          }
          if (!['ACTIVE','FILLED','ENTERED','WATCHING'].includes((msg.status||'').toUpperCase())) return;
        }
        if (msg.type === 'status_line') {
          const ticker = msg.ticker;
          // Active status line → also update positionMap so right column dot lights up
          if (msg.is_active) {
            setPositionMap(prev => ({ ...prev, [ticker]: 'ACTIVE' }));
          }
          setFeedLog(prev => {
            const entry = {
              id:       `s-${ticker}-${Date.now()}-${Math.random()}`,
              type:     'status',
              ticker,
              text:     msg.text || '',
              isActive: msg.is_active || false,
            };
            const next = [...prev, entry];
            return next.length > 500 ? next.slice(-500) : next;
          });
          return;
        }

        if (msg.type !== 'candle_update') return;
        const { ticker, candle } = msg;
        setWsHasData(true);
        const etMatch = (candle.time || '').match(/T(\d{2}:\d{2})/);

        // Push candle entry then a reconstructed status line (mirrors console output exactly)
        setFeedLog(prev => {
          const candelEntry = {
            id:     `${ticker}-${candle.time}-${Date.now()}`,
            type:   'candle',
            time:   etMatch?.[1] || '',
            ticker,
            o: candle.open, h: candle.high, l: candle.low, c: candle.close, v: candle.volume,
          };
          const entries = [candelEntry];

          const plan = plansRef.current.find(p => p.symbol === ticker);
          if (plan) {
            const isActive = positionMapRef.current[ticker] === 'ACTIVE';
            let statusText;
            if (isActive) {
              const dirMult   = (plan.direction || 'LONG') === 'LONG' ? 1 : -1;
              const riskPerSh = Math.abs((plan.entry_price || 0) - (plan.stop_price || 0));
              const pnl       = dirMult * (candle.close - (plan.entry_price || 0)) * (plan.shares || 0);
              const r         = riskPerSh > 0
                ? (dirMult * (candle.close - (plan.entry_price || 0))) / riskPerSh : 0;
              statusText = `$${candle.close.toFixed(2)}  P&L $${pnl.toFixed(2)}  ${r.toFixed(2)}R  [ACTIVE]`;
            } else {
              const actPrice = plan.activation_price || plan.entry_price || 0;
              statusText = `$${candle.close.toFixed(2)} — not at activation ($${actPrice.toFixed(2)})`;
            }
            entries.push({
              id: `s-${ticker}-${Date.now()}`, type: 'status',
              ticker, text: statusText, isActive,
            });
          }

          const next = [...prev, ...entries];
          return next.length > 500 ? next.slice(-500) : next;
        });
        setTickerMap(prev => {
          const existing = prev[ticker] || { price: null, open: null, change: 0, candles: [] };
          const candles  = [...existing.candles];
          const chartTime = toChartTime(candle.time);
          const last = candles[candles.length - 1];
          if (last && last.chartTime === chartTime) {
            candles[candles.length - 1] = { ...last, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume };
          } else {
            candles.push({ chartTime, open: candle.open, high: candle.high, low: candle.low, close: candle.close, volume: candle.volume });
            if (candles.length > 60) candles.shift();
          }
          const open = existing.open || candle.open;
          const change = open ? ((candle.close - open) / open) * 100 : 0;
          return { ...prev, [ticker]: { ...existing, price: candle.close, open, change, candles } };
        });
      } catch (e) {}
    };
    wsRef.current = ws;
    return () => { if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE]);

  const handleFetchPlans = async () => {
    setPlansLoading(true); setPlansError(null); setPlans([]);
    try {
      const res = await fetch(`${API_BASE}/api/v1/stage4/plans`, { headers: { 'X-API-Key': API_KEY } });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || `HTTP ${res.status}`); }
      const data = await res.json();
      const loaded = data.plans || [];
      plansRef.current = loaded; setPlans(loaded); setPlansFile(data.source_file || null);
    } catch (e) { setPlansError(e.message); }
    finally     { setPlansLoading(false); }
  };

  const handleSelectTicker = useCallback((ticker) => {
    if (ticker === selectedTicker) return;
    setSelectedTicker(ticker);
    const plan = plansRef.current.find(p => p.symbol === ticker);
    if (plan) setActiveLevels({ entry: plan.entry_price, stop: plan.stop_price, t1: plan.t1_price, t2: plan.t2_price, t3: plan.t3_price, direction: plan.direction, conviction: plan.conviction, source: 'm12' });
    else setActiveLevels(null);
  }, [selectedTicker]);

  // ── Remove a single ticker from the watchlist ─────────────────────────────────
  const removeTicker = (ticker) => {
    setTickerMap(prev => { const n = { ...prev }; delete n[ticker]; return n; });
    setClosedTickers(prev => { const n = { ...prev }; delete n[ticker]; return n; });
    if (selectedTicker === ticker) { setSelectedTicker(null); setActiveLevels(null); }
  };

  // ── Clear entire session state ────────────────────────────────────────────────
  const clearSession = () => {
    setTickerMap({}); setClosedTickers({}); setFeedLog([]); setPositionMap({});
    setSelectedTicker(null); setActiveLevels(null);
  };

  useEffect(() => {
    if (!chartReady || !selectedTicker || !chartContainerRef.current || !window.LightweightCharts) return;
    const chart = window.LightweightCharts.createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth, height: chartContainerRef.current.clientHeight,
      layout: { background: { color: C.ink }, textColor: C.textLo },
      grid: { vertLines: { color: C.haze }, horzLines: { color: C.haze } },
      timeScale: { borderColor: C.haze, timeVisible: true, secondsVisible: false,
        tickMarkFormatter: (time) => { const d = new Date(time * 1000); return `${d.getUTCHours().toString().padStart(2,'0')}:${d.getUTCMinutes().toString().padStart(2,'0')}`; } },
      rightPriceScale: { borderColor: C.haze }, crosshair: { mode: 1 },
    });
    const candleSeries = chart.addCandlestickSeries({
      upColor: C.signal, downColor: C.warn, borderUpColor: C.signal, borderDownColor: C.warn, wickUpColor: C.signal, wickDownColor: C.warn,
      autoscaleInfoProvider: (original) => {
        const res = original(); const levels = activeLevelsRef.current;
        if (!levels) return res;
        const prices = [levels.stop, levels.t1].filter(n => n != null);
        if (!prices.length) return res;
        const levMin = Math.min(...prices), levMax = Math.max(...prices);
        if (!res) { const pad = (levMax - levMin) * 0.2; return { priceRange: { minValue: levMin - pad, maxValue: levMax + pad } }; }
        return { priceRange: { minValue: Math.min(res.priceRange.minValue, levMin), maxValue: Math.max(res.priceRange.maxValue, levMax) } };
      },
    });
    const vwapSeries = chart.addLineSeries({ color: C.rule, lineWidth: 1, priceLineVisible: false, lastValueVisible: false, title: 'VWAP' });
    chartRef.current = chart; candleSeriesRef.current = candleSeries; vwapSeriesRef.current = vwapSeries;
    setChartCreated(true);
    const handleResize = () => { if (chartContainerRef.current) chart.applyOptions({ width: chartContainerRef.current.clientWidth, height: chartContainerRef.current.clientHeight }); };
    window.addEventListener('resize', handleResize);
    return () => { window.removeEventListener('resize', handleResize); clearPriceLines(); chart.remove(); chartRef.current = null; candleSeriesRef.current = null; vwapSeriesRef.current = null; setChartCreated(false); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartReady, selectedTicker]);

  useEffect(() => {
    if (!chartCreated || !selectedTicker || !candleSeriesRef.current) return;
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/v1/stage4/candles/${selectedTicker}`, { headers: { 'X-API-Key': API_KEY } });
        if (!res.ok) return;
        const data = await res.json();
        if (data.candles?.length > 0 && candleSeriesRef.current) {
          candleSeriesRef.current.setData(data.candles.map(c => ({ time: toChartTime(c.time), open: c.open, high: c.high, low: c.low, close: c.close })));
          let cumTPV = 0, cumVol = 0;
          const vp = data.candles.map(c => { const tp = (c.high+c.low+c.close)/3; cumTPV+=tp*(c.volume||0); cumVol+=(c.volume||0); return { time: toChartTime(c.time), value: cumVol>0?cumTPV/cumVol:tp }; });
          if (vwapSeriesRef.current) vwapSeriesRef.current.setData(vp);
          if (chartRef.current) chartRef.current.timeScale().fitContent();
          const first = data.candles[0], last = data.candles[data.candles.length-1];
          setTickerMap(prev => { const e = prev[selectedTicker]||{}; const o = first.open; return { ...prev, [selectedTicker]: { ...e, open: o, change: ((last.close-o)/o)*100, price: last.close } }; });
        }
      } catch (e) {}
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartCreated, selectedTicker]);

  useEffect(() => {
    if (!chartCreated || !selectedTicker || !candleSeriesRef.current) return;
    const candles = tickerMap[selectedTicker]?.candles;
    if (!candles?.length) return;
    const last = candles[candles.length-1]; if (!last) return;
    try { candleSeriesRef.current.update({ time: last.chartTime, open: last.open, high: last.high, low: last.low, close: last.close }); } catch (e) {}
    if (vwapSeriesRef.current) {
      let cumTPV=0,cumVol=0;
      const vp = candles.map(c => { const tp=(c.high+c.low+c.close)/3; cumTPV+=tp*(c.volume||0); cumVol+=(c.volume||0); return { time: c.chartTime, value: cumVol>0?cumTPV/cumVol:tp }; });
      try { vwapSeriesRef.current.setData(vp); } catch (e) {}
    }
    if (chartRef.current) chartRef.current.timeScale().scrollToPosition(0, false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickerMap, selectedTicker, chartCreated]);

  useEffect(() => {
    if (!chartCreated || !candleSeriesRef.current) return;
    clearPriceLines(); if (activeLevels) drawPriceLines(activeLevels);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLevels, chartCreated]);

  const clearPriceLines = () => {
    if (!candleSeriesRef.current) return;
    Object.values(priceLineRefs.current).forEach(l => { try { candleSeriesRef.current.removePriceLine(l); } catch (e) {} });
    priceLineRefs.current = {};
  };

  const drawPriceLines = (levels) => {
    if (!candleSeriesRef.current || !levels) return;
    const add = (key, price, color, lineStyle, title) => {
      if (!price) return;
      priceLineRefs.current[key] = candleSeriesRef.current.createPriceLine({ price, color, lineWidth: 2, lineStyle, axisLabelVisible: true, title });
    };
    add('entry', levels.entry, C.signal, 2, 'Entry');
    add('stop',  levels.stop,  C.warn,   0, 'Stop');
    add('t1',    levels.t1,    C.rule,   2, 'T1');
    add('t2',    levels.t2,    C.rule,   2, 'T2');
    add('t3',    levels.t3,    C.rule,   2, 'T3');
    if (chartRef.current) chartRef.current.timeScale().fitContent();
  };

  const fmt        = (n) => n != null ? `$${Number(n).toFixed(2)}` : '—';
  const fmtPrice   = (n) => n != null ? `$${Number(n).toFixed(2)}` : '—';
  const fmtVol     = (v) => { if (!v) return '—'; if (v>=1e6) return `${(v/1e6).toFixed(1)}M`; if (v>=1e4) return `${(v/1e3).toFixed(0)}K`; if (v>=1e3) return `${(v/1e3).toFixed(1)}K`; return v.toLocaleString(); };
  const changeColor   = (c) => c>0?'text-signal':c<0?'text-warn':'text-text-lo';
  const convictionBadge = (c) => { if (!c) return 'bg-haze text-text-lo'; const u=c.toUpperCase(); if (u==='HIGH') return 'bg-signal/10 text-signal border border-signal/30'; if (u==='MEDIUM'||u==='MEDIUM-HIGH') return 'bg-rule/10 text-rule border border-rule/30'; return 'bg-haze text-text-lo border border-haze'; };
  const directionBadge  = (d) => !d?'bg-haze text-text-lo':d==='LONG'?'bg-signal/10 text-signal border border-signal/30':'bg-warn/10 text-warn border border-warn/30';

  const selectedData  = selectedTicker ? tickerMap[selectedTicker] : null;
  const tickerList    = Object.keys(tickerMap).sort();
  const activeTickers = tickerList.filter(t => !closedTickers[t]);
  const closedList    = Object.keys(closedTickers).sort();

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col bg-ink text-text-hi overflow-hidden font-mono" style={{ height: 'calc(100vh - 56px)' }}>

      {/* ── TOP HEADER BAR ── */}
      <div className="flex items-center justify-between px-5 py-2.5 border-b border-haze bg-ink-soft flex-shrink-0">
        <div className="flex items-center gap-4 min-w-0">
          <span className="text-[10px] tracking-[0.2em] text-text-lo uppercase">Live Candles</span>
          <div className="h-4 w-px bg-haze" />
          {selectedTicker ? (
            <>
              <span className="text-lg font-bold text-text-hi">{selectedTicker}</span>
              {selectedData?.price != null && (
                <>
                  <span className={`text-xl font-bold ${changeColor(selectedData.change)}`}>{fmtPrice(selectedData.price)}</span>
                  <span className={`text-sm ${changeColor(selectedData.change)}`}>{selectedData.change>=0?'+':''}{selectedData.change.toFixed(2)}%</span>
                </>
              )}
              <span className="text-xs text-rule/70 ml-1">VWAP</span>
              {activeLevels && (
                <div className="flex items-center gap-3 ml-2 text-xs">
                  <span className="text-haze">|</span>
                  {activeLevels.direction && <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${directionBadge(activeLevels.direction)}`}>{activeLevels.direction}</span>}
                  {activeLevels.conviction && <span className={`px-1.5 py-0.5 rounded text-xs ${convictionBadge(activeLevels.conviction)}`}>{activeLevels.conviction}</span>}
                  <span className="text-text-lo">Entry <span className="text-signal">{fmt(activeLevels.entry)}</span></span>
                  <span className="text-text-lo">Stop <span className="text-warn">{fmt(activeLevels.stop)}</span></span>
                  <span className="text-text-lo">T1 <span className="text-rule">{fmt(activeLevels.t1)}</span></span>
                </div>
              )}
            </>
          ) : (
            <span className="text-text-lo/50 text-sm">No ticker selected</span>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="flex items-center gap-1.5">
            <div className={`h-1.5 w-1.5 rounded-full ${isWsConnected ? 'bg-signal animate-pulse-soft' : 'bg-text-lo/30'}`} />
            <span className="text-[10px] tracking-widest text-text-lo">WS</span>
          </div>
          <div className="h-4 w-px bg-haze" />
        </div>
      </div>

      {/* ── MAIN BODY: feed | chart | watchlist ── */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── CANDLE FEED (left column) ── */}
        <div className="w-56 flex-shrink-0 border-r border-haze bg-ink flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-haze/50 flex-shrink-0">
            <span className="text-[10px] tracking-[0.15em] text-text-lo/50 uppercase">Candle Feed</span>
            <span className="text-[10px] text-text-lo/25">{feedLog.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto px-2 py-1">
            {feedLog.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <span className="text-[10px] tracking-widest text-text-lo/20">AWAITING CANDLES</span>
              </div>
            ) : (
              feedLog.map(entry => {
                if (entry.type === 'status') {
                  return (
                    <div key={entry.id} className="pb-[3px] border-b border-haze/20">
                      <div className="flex items-center gap-2 text-[10px]">
                        <span className={`font-semibold w-10 flex-shrink-0 ${entry.isActive ? 'text-signal/80' : 'text-rule/60'}`}>
                          {entry.ticker}
                        </span>
                        <span className={entry.isActive ? 'text-signal/70' : 'text-text-lo/35'}>
                          {entry.text}
                        </span>
                      </div>
                    </div>
                  );
                }
                // Candle entry
                return (
                  <div key={entry.id} className="py-[3px] border-b border-haze/20">
                    <div className="flex items-center justify-between text-[10px]">
                      <span className="text-rule/80 font-semibold">{entry.ticker}</span>
                      <span className="text-text-lo/40">{entry.time}</span>
                      <span className="text-text-lo/30">{fmtVol(entry.v)}</span>
                    </div>
                    <div className="text-[10px] text-text-lo/50 mt-0.5">
                      O:<span className="text-text-lo/80">{entry.o?.toFixed(2)}</span>{' '}
                      H:<span className="text-signal/70">{entry.h?.toFixed(2)}</span>{' '}
                      L:<span className="text-warn/70">{entry.l?.toFixed(2)}</span>{' '}
                      C:<span className="text-text-hi/80">{entry.c?.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={feedEndRef} />
          </div>
        </div>

        {/* ── CHART COLUMN (full height now) ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

          {/* Plans control bar */}
          <div className="flex items-center gap-3 px-4 py-2 border-b border-haze bg-ink-soft/50 flex-shrink-0 flex-wrap">
            <span className="text-[10px] tracking-[0.15em] text-text-lo uppercase">M12 Plans</span>
            <button onClick={handleFetchPlans} disabled={plansLoading} className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${plansLoading?'bg-haze text-text-lo cursor-not-allowed':'btn-amber'}`}>
              {plansLoading ? 'Loading…' : 'Fetch Plans'}
            </button>
            {plansError && <span className="text-warn text-xs">⚠ {plansError}</span>}
            {plansFile && !plansError && <span className="text-text-lo text-xs">{plansFile}</span>}
            {plans.length > 0 && !plansError && <span className="text-text-lo/60 text-xs">{plans.length} plans loaded — click a ticker to apply levels</span>}
            {activeLevels && selectedTicker && (
              <button onClick={() => { setActiveLevels(null); clearPriceLines(); }} className="ml-auto text-text-lo/50 hover:text-warn text-xs transition-colors">
                ✕ Clear levels
              </button>
            )}
          </div>

          {/* Chart — now full height with no feed below */}
          <div className="flex-1 min-h-0 relative bg-ink">
            {chartReady ? (
              selectedTicker ? (
                <div ref={chartContainerRef} className="w-full h-full" />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  {tickerList.length === 0 ? (
                    <>
                      <div className="text-text-lo/50 text-sm mb-1">{isWsConnected ? 'Waiting for agent data…' : 'Connecting to feed…'}</div>
                      <div className="text-text-lo/30 text-xs">{isWsConnected ? 'Tickers will appear on the right as agents send candles' : 'Ensure the API is running and agents are active'}</div>
                    </>
                  ) : (
                    <>
                      <div className="text-text-lo/50 text-sm mb-1">Select a ticker</div>
                      <div className="text-text-lo/30 text-xs">Click a ticker in the watchlist →</div>
                    </>
                  )}
                </div>
              )
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-text-lo/40 text-xs tracking-widest">LOADING CHART LIBRARY…</div>
              </div>
            )}
          </div>
        </div>

        {/* ── WATCHLIST (right) ── */}
        <div className="w-56 flex-shrink-0 flex flex-col border-l border-haze bg-ink-soft">

          {/* Header with clear-session button */}
          <div className="px-4 py-3 border-b border-haze flex-shrink-0">
            <div className="flex items-center justify-between">
              <span className="text-[10px] tracking-[0.2em] text-text-lo uppercase">Watchlist</span>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-text-lo/40">{activeTickers.length} active</span>
                {(tickerList.length > 0 || closedList.length > 0) && (
                  <button
                    onClick={clearSession}
                    title="Clear all tickers and reset session"
                    className="text-[10px] text-text-lo/30 hover:text-warn transition-colors tracking-wider"
                  >
                    RESET
                  </button>
                )}
              </div>
            </div>
          </div>

          {activeTickers.length > 0 && (
            <div className="flex items-center justify-between px-4 py-1.5 border-b border-haze/50 flex-shrink-0">
              <span className="text-[10px] text-text-lo/40">Symbol</span>
              <div className="flex items-center gap-4">
                <span className="text-[10px] text-text-lo/40 w-14 text-right">Price</span>
                <span className="text-[10px] text-text-lo/40 w-14 text-right">Chg %</span>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto">

            {activeTickers.length === 0 && closedList.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                <div className="text-text-lo/30 text-xs leading-relaxed">
                  {isWsConnected ? 'Waiting for candle data…' : 'Connecting to feed…'}
                </div>
              </div>
            ) : (
              activeTickers.map(ticker => {
                const tData      = tickerMap[ticker];
                const isSelected = ticker === selectedTicker;
                const plan       = plans.find(p => p.symbol === ticker);
                const change     = tData?.change ?? 0;
                const chgColor   = changeColor(change);
                return (
                  // Two sibling buttons inside a group div — avoids invalid nested <button>
                  <div
                    key={ticker}
                    className={`group flex items-center border-b border-haze/50 ${isSelected ? 'bg-haze border-l-2 border-l-rule' : 'hover:bg-haze/50 border-l-2 border-l-transparent'}`}
                  >
                    {/* Main ticker select area */}
                    <button onClick={() => handleSelectTicker(ticker)} className="flex-1 flex items-center justify-between px-4 py-3 text-left min-w-0">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className={`text-sm font-bold ${isSelected ? 'text-text-hi' : 'text-text-hi/70'}`}>{ticker}</span>
                          {plan?.direction && <span className={`text-xs font-bold ${plan.direction==='LONG'?'text-signal':'text-warn'}`}>{plan.direction==='LONG'?'▲':'▼'}</span>}
                          {positionMap[ticker] === 'ACTIVE'
                            ? <span className="h-1.5 w-1.5 rounded-full bg-signal animate-pulse-soft flex-shrink-0" title="Active trade" />
                            : <span className="h-1.5 w-1.5 rounded-full bg-text-lo/20 flex-shrink-0" title="Watching" />
                          }
                        </div>
                        {plan?.conviction && <span className="text-[10px] text-text-lo/50">{plan.conviction}</span>}
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`text-sm font-medium ${isSelected ? 'text-text-hi' : 'text-text-hi/70'}`}>{tData?.price!=null?fmtPrice(tData.price):'—'}</span>
                        <span className={`text-xs ${chgColor}`}>{change>=0?'+':''}{change.toFixed(2)}%</span>
                      </div>
                    </button>
                    {/* Per-ticker remove button — visible on row hover */}
                    <button
                      onClick={() => removeTicker(ticker)}
                      title={`Remove ${ticker} from watchlist`}
                      className="opacity-0 group-hover:opacity-100 transition-opacity pr-3 text-[11px] text-text-lo/30 hover:text-warn flex-shrink-0"
                    >
                      ✕
                    </button>
                  </div>
                );
              })
            )}

            {/* Unsubscribed section */}
            {closedList.length > 0 && (
              <>
                <div className="px-4 py-2 border-t border-haze/50 mt-1">
                  <span className="text-[10px] tracking-widest text-text-lo/30 uppercase">Unsubscribed</span>
                </div>
                {closedList.map(ticker => {
                  const info = closedTickers[ticker];
                  return (
                    <div key={ticker} className="group flex items-center border-b border-haze/30 border-l-2 border-l-transparent opacity-50">
                      <div className="flex-1 flex items-center justify-between px-4 py-2.5">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-bold text-text-lo">{ticker}</span>
                          <span className={`text-[10px] ${info.reason==='SKIP'||info.reason==='SKIPPED'?'text-warn/70':'text-text-lo/50'}`}>{info.reason}</span>
                        </div>
                        <span className="text-[10px] text-text-lo/30">{info.time}</span>
                      </div>
                      <button onClick={() => removeTicker(ticker)} className="opacity-0 group-hover:opacity-100 transition-opacity pr-3 text-[11px] text-text-lo/30 hover:text-warn flex-shrink-0">
                        ✕
                      </button>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="px-4 py-2.5 border-t border-haze text-[10px] tracking-wider text-text-lo/40 flex-shrink-0">
            {wsHasData
              ? `${activeTickers.length} active${closedList.length > 0 ? ` · ${closedList.length} closed` : ''}`
              : isWsConnected ? 'Waiting for agents…' : 'Feed disconnected'}
          </div>
        </div>

      </div>
    </div>
  );
}
